import {
  Component,
  Input,
  Output,
  EventEmitter,
  forwardRef,
  OnInit,
  ViewChild,
  ViewEncapsulation,
  ElementRef,
  AfterViewInit,
} from "@angular/core";
import {
  ControlValueAccessor,
  NG_VALUE_ACCESSOR,
  NG_VALIDATORS,
  Validator,
  AbstractControl,
  FormGroup,
  FormControl,
} from "@angular/forms";
import { MAT_RIPPLE_GLOBAL_OPTIONS, RippleGlobalOptions } from "@angular/material/core";
import { MatDatepicker } from "@angular/material/datepicker";
import { MatDateRangePicker } from "@angular/material/datepicker";
import { StartView } from "../types/input-date.types";

const rippleConfig: RippleGlobalOptions = {
  disabled: true,
};

@Component({
  selector: "ds-date-input",
  templateUrl: "./ds-date-input.component.html",
  styleUrls: ["./ds-date-input.component.scss"],
  encapsulation: ViewEncapsulation.None,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => DsDateInputComponent),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => DsDateInputComponent),
      multi: true,
    },
    { provide: MAT_RIPPLE_GLOBAL_OPTIONS, useValue: rippleConfig },
  ],
})
export class DsDateInputComponent implements ControlValueAccessor, Validator, OnInit, AfterViewInit {
  @Input() label: string = "";
  @Input() placeholder: string = "";
  @Input() hintText: string = "";
  @Input() minDate?: Date;
  @Input() maxDate?: Date;
  @Input() disabled = false;
  @Input() range = false;
  @Input() required = false;
  @Input() startView: StartView = "month";
  @Input() touchUi = false;
  @Input() error = false;
  @Input() startAt?: Date;
  @Input() placeholderStartDate: string = "Fecha inicio";
  @Input() placeholderEndDate: string = "Fecha fin";
  @Input() datepickerFilter?: (date: Date | null) => boolean;

  @Output() dateChange = new EventEmitter<Date | { start: Date; end: Date }>();
  @Output() dateInput = new EventEmitter<any>();
  @Output() monthSelected = new EventEmitter<Date>();
  @Output() openedDatepicker = new EventEmitter<void>();
  @Output() closedDatepicker = new EventEmitter<void>();
  @Output() yearSelected = new EventEmitter<Date>();
  @Output() selectedChanged = new EventEmitter<Date>();

  @ViewChild("singlePicker") singlePicker!: MatDatepicker<Date>;
  @ViewChild("rangePicker") rangePicker!: MatDateRangePicker<Date>;

  value: any;
  formGroup!: FormGroup;
  forceClass: string = "";

  constructor(private elementRef: ElementRef) {}

  ngOnInit() {
    if (this.range) {
      this.formGroup = new FormGroup({
        start: new FormControl(),
        end: new FormControl(),
      });
      if (this.value) {
        this.writeValue(this.value);
      }
    }
  }

  ngAfterViewInit() {
    this.detectHostClasses();
  }

  private detectHostClasses() {
    const hostElement = this.elementRef.nativeElement;
    const classList = hostElement.classList;
    if (classList.contains("force-desktop")) {
      this.forceClass = "force-desktop";
    } else if (classList.contains("force-mobile")) {
      this.forceClass = "force-mobile";
    }
  }

  onChange = (_: any) => {};
  onTouched = () => {};

  writeValue(value: any): void {
    this.value = value;
    if (this.range && this.formGroup && value) {
      if (value.start) {
        this.formGroup.get("start")?.setValue(value.start);
      }
      if (value.end) {
        this.formGroup.get("end")?.setValue(value.end);
      }
    }
  }
  registerOnChange(fn: any): void {
    this.onChange = fn;
  }
  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }
  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }
  validate(control: AbstractControl) {
    return null;
  }

  handleDateChange(event: any) {
    this.value = event.value || event;
    this.onChange(this.value);
    this.dateChange.emit(this.value);
  }

  handleMonthSelected(event: Date) {
    this.value = event;
    this.onChange(event);
    this.monthSelected.emit(event);
    this.dateChange.emit(event);
  }

  handleStartDateChange(event: any) {
    const startDate = event.value || event;
    this.value = { ...this.value, start: startDate };
    this.onChange(this.value);
    this.dateChange.emit(this.value);

    if (this.formGroup) {
      this.formGroup.get("start")?.setValue(startDate);
    }
  }

  handleEndDateChange(event: any) {
    const endDate = event.value || event;
    this.value = { ...this.value, end: endDate };
    this.onChange(this.value);
    this.dateChange.emit(this.value);
    if (this.formGroup) {
      this.formGroup.get("end")?.setValue(endDate);
    }
  }

  handleStartDateInput(event: any) {
    this.dateInput.emit({ ...event, type: "start" });
  }

  handleEndDateInput(event: any) {
    this.dateInput.emit({ ...event, type: "end" });
  }
}
